import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PendingIcon from '@mui/icons-material/Pending'
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab'
import { Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { BasePickerProps } from '@mui/x-date-pickers/internals'
import { GreenText, LoadingContent } from 'components'
import { getStatusChecklsit } from 'controllers/vehicles/statusChecklist'
import { VehicleDto } from 'dtos'
import { VehicleStatusChecklistDto, VehicleStatusChecklistItemDto } from 'dtos/vehicles/statusChecklist'
import { useEffect, useState } from 'react'
import { formatDate, formatDateTime } from 'utils'

interface StatusChecklistItemProps {
  label: string
  statusChecklistItem?: VehicleStatusChecklistItemDto
  connector?: boolean
  dateOnly?: boolean
}

function StatusChecklistItem({ label, statusChecklistItem, connector = false, dateOnly = false }: StatusChecklistItemProps) {
  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineDot
          sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          color={statusChecklistItem?.isComplete ? 'primary' : 'grey'}
        >
          {statusChecklistItem?.isComplete ? <CheckCircleIcon sx={{ fontSize: 32 }} /> : <PendingIcon sx={{ fontSize: 32 }} />}
        </TimelineDot>

        {connector && <TimelineConnector />}
      </TimelineSeparator>

      <TimelineContent>
        <Typography variant='body1' color='textSecondary' fontWeight='bold'>
          {label}
        </Typography>

        <Typography variant='body1' color='textSecondary' fontWeight='regular'>
          {statusChecklistItem?.isComplete ? (
            <>
              <Typography>
                {dateOnly ? formatDate(statusChecklistItem?.timestamp) : formatDateTime(statusChecklistItem?.timestamp!)}
              </Typography>

              {statusChecklistItem?.firstName && statusChecklistItem?.lastName && (
                <Typography>
                  by {statusChecklistItem?.firstName} {statusChecklistItem?.lastName}
                </Typography>
              )}
            </>
          ) : (
            <Typography>TBD</Typography>
          )}
        </Typography>
      </TimelineContent>
    </TimelineItem>
  )
}

interface VehicleStatusCheckListDetailsCardProps {
  vehicle: VehicleDto
}

export default function VehicleStatusCheckListDetailsCard({ vehicle }: VehicleStatusCheckListDetailsCardProps) {
  const [statusChecklist, setStatusChecklist] = useState<VehicleStatusChecklistDto | undefined>(undefined)
  const [isGetting, setIsGetting] = useState<boolean>(false)

  useEffect(() => {
    if (!vehicle.vehicleGuid) return

    setIsGetting(true)
    getStatusChecklsit(vehicle.vehicleGuid)
      .then(res => setStatusChecklist(res.value))
      .finally(() => setIsGetting(false))
  }, [vehicle.vehicleGuid])

  return (
    <Card variant='outlined'>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h6'>
              Vehicle Status Checklist
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <LoadingContent isLoading={isGetting}>
            <Grid item xs={12}>
              <Timeline position='alternate'>
                <StatusChecklistItem label='Approved for Shipment' statusChecklistItem={statusChecklist?.approvedForShipment} connector />

                <StatusChecklistItem
                  label='Vehicle Check-In Complete'
                  statusChecklistItem={statusChecklist?.vehicleCheckInComplete}
                  dateOnly
                  connector
                />

                <StatusChecklistItem label='Recalls Complete' statusChecklistItem={statusChecklist?.recallsComplete} connector />

                <StatusChecklistItem label='Cluster Complete' statusChecklistItem={statusChecklist?.clusterComplete} connector />

                <StatusChecklistItem label='Title Application Sent to SoS' statusChecklistItem={statusChecklist?.titleApplicationSentToSoS} connector />

                <StatusChecklistItem label='Title Received from SoS' statusChecklistItem={statusChecklist?.titleReceivedFromSoS} connector />

                <StatusChecklistItem label='Submitted to DOT' statusChecklistItem={statusChecklist?.submittedToDOT} connector />

                <StatusChecklistItem label='Bond Released' statusChecklistItem={statusChecklist?.bondReleased} connector />

                <StatusChecklistItem label='Signed BOL Upload' statusChecklistItem={statusChecklist?.signedBOLUpload} connector />

                <StatusChecklistItem label='Invoiced' statusChecklistItem={statusChecklist?.invoiced} />
              </Timeline>
            </Grid>
          </LoadingContent>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Approved for Shipment:**

The "Approved for Shipment" checkbox is marked when “Send To Broker/Transporter" is pressed for the Inbound Trip the vehicle is associated with.

**Vehicle Check-In Complete:**

The "Vehicle Check-In Complete" checkbox is marked when a user completes the Vehicle Check-In workflow by pressing the final "Submit" button on the Check-In Review step.

   - The date displayed matches the Vehicle Check-In Date on the Vehicle Details card under the Vehicle Check-In tab.
   - The user displayed is the person who pressed Submit, recorded on the system-generated Import Charge transaction created at check-in completion.

**Recalls Complete:**

The "Recalls Complete" checkbox is marked under the following conditions:

   - If there are no recall campaigns associated with the vehicle, and the "Recall Docs" have been uploaded to confirm there are no open recalls
   on the VIN.
   - For recall campaigns marked as "Repaired in CA", all campaigns must have a Recall Status of "Repaired", accompanied by uploaded "Recall
   Docs" (OEM site screenshot), and the "RO" from the Exporter uploaded to the recall campaign for the "Recall Complete" checkbox to be
   marked.
   - For recall campaigns marked as "Recalls to be Repaired in US", all campaigns must have a Recall Status of "Repaired", accompanied by
   uploaded "Recall Docs" (OEM site screenshot), and the "RO" from the authorized dealership that performed the recall work in the US
   uploaded to the recall campaign for the "Recall Complete" checkbox to be marked.

**Cluster Complete:**

The "Cluster Complete" checkbox is marked under the following conditions:

   - If "Cluster Conversion" equals No during vehicle check-in.

   - If "Cluster Conversion" equals Yes and a cluster has been reinstalled, the user is required to capture photos of:

  The Importer Label
   The Manufacturer Label along with the Importer Label ("Combo")
   The Installed Cluster

All three images must be uploaded for the "Cluster Complete" checkbox to be marked.

**Title Application Sent to SoS:**

The "Title Application Sent to SoS" checkbox is marked under the following conditions:

For vehicles with Title Processing State = Michigan, the checkbox is marked when:
   - A cover letter photo has been taken of the paperwork, with the cover letter placed on top inside the mailbox at the Michigan Secretary of
   State (SoS) office.

   - A Michigan Title Application Receipt has been uploaded.

   - (Michigan Titles are OCR’d and associated with the respective VIN).

For vehicles with Title Processing State = Ohio, the checkbox is marked if the vehicle has achieved the "Received in Ohio" status based on
FedEx API indicating the package has been delivered to Ohio

**Title Received from SoS:**

The “Title Received from SoS” checkbox is checked when vehicles have their new Title document OCR'd and associated with the VIN.
Because a vehicle may have multiple titles associated with it, the “Title Received from SoS” date/time reflects when the most recent Title
document was associated with the vehicle. If the vehicle’s Title Application State = Michigan and “Registered Importer Will Not Receive Title”
is checked, the “Title Received from SoS” checkbox is checked when the vehicle has a Michigan Title Application Receipt document OCR’d
and associated with the VIN.

**Submitted to DOT:**

The "Submitted to DOT" checkbox is marked once a user manually confirms the upload of conformity documents to the DOT portal.

   - The displayed date and time indicate when the user confirmed the vehicle's documents were uploaded to the DOT portal.
   - This timestamp initiates the 30-day hold period as mandated by the DOT.

**Bond Released:**

The “Bond Released” checkbox is marked when the Bond Release document has been OCR'd and associated with the VIN

**Signed BOL Uploaded:**

The "Signed BOL Uploaded" checkbox is marked when the signed Bill of Lading (BOL) is associated with VINs that match the Outbound Load
ID.

This occurs after the signed BOL has been OCR'd and linked to the Outbound Trip.

**Invoiced:**

The “Invoiced” checkbox is marked when a user has indicated that the invoicing process is complete for this VIN.`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
